import { useMemo, useState } from 'react';
import {
  CommandEmpty,
} from '@/components/ui/command';
import {
  SearchableDropdown,
  type SearchableDropdownProps,
} from '@/components/ui/searchable-dropdown';
import { DropdownTrigger } from '@/react/components/ui/dropdown-trigger';
import { DropdownListItem } from '@/react/components/ui/dropdown-list-item';
import { SearchableDropdownCreateButton } from '@/react/components/ui/searchable-dropdown-create-button';
import { useTranslation } from '@hooks/useTranslation';
import type { ContactBillingAddress } from '@api/contacts';
import { HighlightMatch } from '@components/common/HighlightMatch';

interface BillingAddressComboboxProps {
  billingAddresses: ContactBillingAddress[];
  selectedId?: number | string | null;
  onSelect: (billingAddress: ContactBillingAddress) => void;
  onCreateNew?: () => void;
  disabled?: boolean;
  collisionPadding?: SearchableDropdownProps['collisionPadding'];
  // What the trigger displays for the selected row. 'name' (default) falls back
  // to the street when unnamed, matching Create Order. 'address' always shows
  // the street — for callers that show the name in a field of its own, so the
  // picker isn't a second place the same name-vs-street ambiguity can appear.
  triggerField?: 'name' | 'address';
}

function matchesSearch(billingAddress: ContactBillingAddress, query: string) {
  if (!query) return true;
  const lowerQuery = query.toLowerCase();
  return (
    (billingAddress.name ?? '').toLowerCase().includes(lowerQuery) ||
    billingAddress.address.toLowerCase().includes(lowerQuery) ||
    billingAddress.postal_code.toLowerCase().includes(lowerQuery)
  );
}

export function BillingAddressCombobox({
  billingAddresses,
  selectedId,
  onSelect,
  onCreateNew,
  disabled = false,
  collisionPadding,
  triggerField = 'name',
}: BillingAddressComboboxProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedAddress = billingAddresses.find(
    (billingAddress) => String(billingAddress.id) === String(selectedId ?? ''),
  );

  const filteredAddresses = useMemo(
    () => billingAddresses.filter((billingAddress) => matchesSearch(billingAddress, searchQuery)),
    [billingAddresses, searchQuery],
  );

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (disabled && nextIsOpen) return;
    setIsOpen(nextIsOpen);
    if (!nextIsOpen) setSearchQuery('');
  };

  const createButton = (
    <SearchableDropdownCreateButton
      label={t('shared.create_billing_address')}
      disabled={!onCreateNew}
      onSelect={() => {
        if (!onCreateNew) return;
        handleOpenChange(false);
        onCreateNew();
      }}
    />
  );

  return (
    <SearchableDropdown
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      searchPlaceholder={t('shared.search.billing_address')}
      onSearchChange={setSearchQuery}
      shouldFilter={false}
      selectedValue={selectedAddress ? String(selectedAddress.id) : undefined}
      collisionPadding={collisionPadding}
    >
      <SearchableDropdown.Trigger>
        <DropdownTrigger
          selectedLabel={triggerField === 'address' ? selectedAddress?.address : (selectedAddress?.name || selectedAddress?.address)}
          placeholder={t('create_order.placeholders.billing_address')}
          isOpen={isOpen}
          disabled={disabled}
        />
      </SearchableDropdown.Trigger>

      <SearchableDropdown.List showFadeGradient maxHeight="256px">
        {filteredAddresses.length === 0 && (
          <CommandEmpty className="px-4 py-3 text-left text-sm text-grey">
            {t('shared.empty.no_billing_addresses')}
          </CommandEmpty>
        )}

        {filteredAddresses.length > 0 && (
          <div className="flex flex-col gap-2 py-2">
            {filteredAddresses.map((billingAddress) => {
              const isSelected = String(billingAddress.id) === String(selectedId ?? '');
              const primaryText = billingAddress.name || billingAddress.address;
              const secondaryText = billingAddress.name
                ? `${billingAddress.address}, ${billingAddress.postal_code}`
                : billingAddress.postal_code;
              return (
                <DropdownListItem
                  key={billingAddress.id}
                  value={String(billingAddress.id)}
                  onSelect={() => {
                    onSelect(billingAddress);
                    handleOpenChange(false);
                  }}
                  isSelected={isSelected}
                  primary={<HighlightMatch text={primaryText} query={searchQuery} />}
                  primaryTitle={primaryText}
                  secondary={<HighlightMatch text={secondaryText} query={searchQuery} />}
                  secondaryTitle={secondaryText}
                />
              );
            })}
          </div>
        )}
      </SearchableDropdown.List>

      <SearchableDropdown.Footer>
        {createButton}
      </SearchableDropdown.Footer>
    </SearchableDropdown>
  );
}
